import React, { useState, useCallback, useEffect } from "react";
import {
  Page,
  Card,
  Modal,
  Text,
  Spinner,
  Image,
  Loading,
  Button,
  DataTable,
  ButtonGroup,
  Toast,
  Checkbox,
  FormLayout,
  Filters,
  TextField,
  Badge,
  Frame,
  InlineError,
  ChoiceList,
  Thumbnail,
  EmptySearchResult,
  Tag,
  Select,
  Popover,
  ActionList,
} from "@shopify/polaris";
import axios from "../../Assets/Lib/axios";
import { useRef } from "react";
import emptyListImage from "../../Assets/Images/emptyList.svg";
import { useNavigate } from "react-router-dom";
import { MultiSelect } from "react-multi-select-component";
// import Select from "react-select";
import makeAnimated from "react-select/animated";
import Pagination from "@mui/material/Pagination";
import EllipsisText from "react-ellipsis-text";
import { useParams } from "react-router-dom";
import Select2 from "react-select";

// Filters are kept for the tab session so that drilling into a course and
// coming back doesn't throw away what you had selected.
const FILTER_STORAGE_KEY = "momtaz.courses.filters";

function readStoredFilters() {
  try {
    return JSON.parse(sessionStorage.getItem(FILTER_STORAGE_KEY)) || {};
  } catch (error) {
    return {};
  }
}

// One row's action menu, collapsed into a single disclosure button so the
// table no longer needs horizontal scrolling to reach it.
function RowActions({ actions }) {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((value) => !value), []);

  return (
    <Popover
      active={open}
      preferredAlignment="right"
      activator={
        <Button onClick={toggle} disclosure accessibilityLabel="Course actions">
          Actions
        </Button>
      }
      onClose={toggle}
    >
      <ActionList
        actionRole="menuitem"
        items={actions.map((action) => ({
          content: action.content,
          onAction: () => {
            setOpen(false);
            action.onAction();
          },
        }))}
      />
    </Popover>
  );
}

const Courses = () => {
  const navigate = useNavigate();
  const { id, subjectId } = useParams();
  const addEditState = useRef("");
  // High enough that the whole catalogue arrives in one page, which keeps the
  // client-side "has videos" filter accurate. Revisit if courses outgrow this.
  const perPage = 250;
  const [ts, setTs] = useState("");
  const [availability, setAvailability] = useState(
    () => readStoredFilters().availability || ""
  );
  const [queryValue, setQueryValue] = useState(
    () => readStoredFilters().query || ""
  );
  const [page, setPage] = useState(() => readStoredFilters().page || 1);
  const [pageObject, setPageObject] = useState({
    isLoading: true,
    isSaving: false,
    tableItems: [],
    tableTotalPages: 0,
    isListEmpty: false,
  });
  const handleAddCourse = () => {
    navigate(`/admin/courses/new`);
  };
  function handleFiltersQueryChange(queryValue) {
    refBoolPage.current = false;
    setQueryValue(queryValue);
  }
  function handleAvailabilityChange(availability) {
    setAvailability(availability);
  }
  const handleAvailabilityRemove = useCallback(() => setAvailability(""), []);
  const handleQueryValueRemove = useCallback(() => setQueryValue(""), []);
  const handleFiltersClearAll = useCallback(() => {
    handleAvailabilityRemove();
    handleQueryValueRemove();
  }, [handleAvailabilityRemove, handleQueryValueRemove]);

  const filters = [
    {
      key: "availability",
      label: "Filter by",
      filter: (
        <ChoiceList
          title="Filter by"
          titleHidden
          choices={[{ label: "Title", value: "title" }]}
          selected={availability || []}
          onChange={handleAvailabilityChange}
        />
      ),
      shortcut: false,
    },
  ];

  const appliedFilters = [];
  if (!isEmpty(availability)) {
    const key = "availability";
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, availability),
      onRemove: handleAvailabilityRemove,
    });
  }
  const loadingMarkup = pageObject.isLoading ? <Loading /> : null;
  const emtyState = pageObject.isListEmpty ? (
    <div
      style={{
        display: "flex",
        justifyContent: "middle",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div style={{ width: "25%" }}>
        <Image src={emptyListImage}></Image>
      </div>
      <div className="mb-3">
        <Text variant="bodyLg" as="p">
          No results found
        </Text>
      </div>
      <br />
    </div>
  ) : (
    pageObject.isLoading && (
      <div className="spinnerContainer">
        <div className="vertical-center">
          <Spinner size="large" />
        </div>
      </div>
    )
  );
  const refBoolPage = useRef(true);
  const handleChangePage = (event, value) => {
    refBoolPage.current = true;
    setPage(value);
  };
  const handleEdit = (id) => {
    navigate(`/admin/courses/${id}`);
  };
  const handleEditPlaylists = (id) => {
    navigate(`/admin/courses/${id}/playlists`);
  };
  const handleEditFiles = (id) => {
    navigate(`/admin/courses/${id}/files`);
  };
  const handleEditQuestions = (id) => {
    navigate(`/admin/courses/${id}/questions-and-answers`);
  };
  const handleEditLectures = (id) => {
    navigate(`/admin/courses/${id}/lectures`);
  };
  const handleEditNotes = (id) => {
    navigate(`/admin/courses/${id}/notes`);
  };
  const handleSubscribers = (id) => {
    navigate(`/admin/courses/${id}/subscribers`);
  };
  const [optionsUniversities, setOptionsUniversities] = useState([]);
  const [selectedUniversity, setSelectedUniversity] = useState(
    () => readStoredFilters().university || ""
  );
  const handleSelectChangeUniversity = (newValue) => {
    refBoolPage.current = false;
    setSelectedUniversity(newValue);
  };
  const [selectedType, setSelectedType] = useState(
    () => readStoredFilters().type || ""
  );
  const handleSelectChangeType = (newValue) => {
    refBoolPage.current = false;
    setSelectedType(newValue);
  };
  const [optionsSubjects, setOptionsSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(
    () => readStoredFilters().subject || ""
  );
  const handleSelectChangeSubject = (newValue) => {
    refBoolPage.current = false;
    setSelectedSubject(newValue);
  };
  const [selectedStatus, setSelectedStatus] = useState(
    () => readStoredFilters().status || ""
  );
  const handleSelectChangeStatus = (newValue) => {
    refBoolPage.current = false;
    setSelectedStatus(newValue);
  };
  // Courses with no recorded video time are flagged in the table so you don't
  // have to open one to find out it's empty.
  const [selectedVideos, setSelectedVideos] = useState(
    () => readStoredFilters().videos || ""
  );
  const handleSelectChangeVideos = (newValue) => {
    refBoolPage.current = false;
    setSelectedVideos(newValue);
  };
  // Server-side sort. The backend accepts id, title, price and created_at.
  const [sortColumnIndex, setSortColumnIndex] = useState(
    () => readStoredFilters().sortColumnIndex ?? undefined
  );
  const [sortDirection, setSortDirection] = useState(
    () => readStoredFilters().sortDirection || "descending"
  );
  const sortableColumns = { 0: "id", 2: "title", 4: "price" };
  const handleSort = useCallback((index, direction) => {
    refBoolPage.current = false;
    setSortColumnIndex(index);
    setSortDirection(direction);
  }, []);
  const sortParam =
    sortColumnIndex !== undefined && sortableColumns[sortColumnIndex]
      ? `${sortDirection === "descending" ? "-" : ""}${
          sortableColumns[sortColumnIndex]
        }`
      : "";
  const hasActiveFilters =
    Boolean(selectedUniversity) ||
    Boolean(selectedType) ||
    Boolean(selectedSubject) ||
    Boolean(selectedStatus) ||
    Boolean(selectedVideos) ||
    Boolean(queryValue);
  const handleClearAllFilters = () => {
    refBoolPage.current = false;
    setSelectedUniversity("");
    setSelectedType("");
    setSelectedSubject("");
    setSelectedStatus("");
    setSelectedVideos("");
    setQueryValue("");
    setAvailability("");
    setSortColumnIndex(undefined);
  };

  // Persist the whole view so returning from a course restores it.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify({
          university: selectedUniversity,
          type: selectedType,
          subject: selectedSubject,
          status: selectedStatus,
          videos: selectedVideos,
          query: queryValue,
          availability,
          page,
          sortColumnIndex,
          sortDirection,
        })
      );
    } catch (error) {
      // Storage can be unavailable in private modes; filters just won't persist.
    }
  }, [
    selectedUniversity,
    selectedType,
    selectedSubject,
    selectedStatus,
    selectedVideos,
    queryValue,
    availability,
    page,
    sortColumnIndex,
    sortDirection,
  ]);
  const [courseName, setCourseName] = useState("");
  const [courseNameError, setCourseNameError] = useState("");
  const handleChangeCourseName = (newValue) => {
    setCourseName(newValue);
    setCourseNameError("");
  };
  const [isSaving, setIsSaving] = useState(false);
  const refId = useRef(null);
  const [activeDuplicate, setActiveDuplicate] = useState(false);
  const handleChangeModalDuplicate = useCallback((activeDuplicate) => {
    setActiveDuplicate(!activeDuplicate);
    setIsSaving(false);
  }, []);
  const handleDuplicate = (id) => {
    refId.current = id;
    setActiveDuplicate(true);
  };
  const handleYesDuplicate = () => {
    if (!courseName) {
      setCourseNameError("Thie field is required");
    } else {
      setIsSaving(true);
      axios
        .post(`courses/${refId.current}/duplicate`, { title: courseName })
        .then((result) => {
          toggleActive();
          setIsSaving(false);
          setActiveDuplicate(false);
          setTs(+new Date());
        })
        .catch((err) => console.log(err));
    }
  };
  useEffect(() => {
    fetchData();
  }, [
    ts,
    availability,
    queryValue,
    page,
    selectedUniversity,
    selectedType,
    selectedSubject,
    selectedStatus,
    selectedVideos,
    sortParam,
  ]);

  // Filter dropdowns only need loading once.
  useEffect(() => {
    async function fetchFilterOptions() {
      try {
        const [universities, subjects] = await Promise.all([
          axios.get(`/universities?per_page=200`),
          axios.get(`/subjects?per_page=500`),
        ]);
        setOptionsUniversities(
          universities.data.data.data.map((item) => ({
            label: getFirstNonNullValue(item.name),
            value: String(item.id),
          }))
        );
        setOptionsSubjects(
          subjects.data.data.data.map((item) => ({
            label: getFirstNonNullValue(item.name),
            value: String(item.id),
          }))
        );
      } catch (error) {
        console.log(error);
      }
    }
    fetchFilterOptions();
  }, []);

  async function fetchData() {
    let responseCourses = "";

    try {
      responseCourses = await axios.get(
        `/courses?per_page=${perPage}&page=${refBoolPage.current ? page : 1}${
          queryValue
            ? `&filter[${
                availability === "" ? "title" : availability
              }]=${queryValue}`
            : ""
        }${selectedType ? `&filter[type]=${selectedType}` : ""}${
          selectedUniversity ? `&filter[university_id]=${selectedUniversity}` : ""
        }${selectedSubject ? `&filter[subject_id]=${selectedSubject}` : ""}${
          selectedStatus ? `&filter[is_active]=${selectedStatus}` : ""
        }${sortParam ? `&sort=${sortParam}` : ""}`
      );
      setPageObject({
        ...pageObject,
        tableItems: applyVideoFilter(responseCourses.data.data.data).map((item, index) => [
          item.id,
          item?.image && <Thumbnail size="extraSmall" source={item.image} />,
          item?.title ? getFirstNonNullValue(item.title) : "-",
          hasVideos(item) ? (
            <Text as="span" tone="subdued">
              {formatDuration(item.time)}
            </Text>
          ) : (
            <Badge tone="critical">No videos</Badge>
          ),
          item?.price !== null ? item.price : "-",
          <div>
            <Badge>{item.type.description}</Badge>{" "}
            {institutionLabel(item)}
          </div>,
          item.subject ? getFirstNonNullValue(item.subject.name) : "-",
          // item.professors && item.professors.length ? (
          //   <div className="rowDiv">
          //     {item.professors.map((item) => {
          //       return (
          //         <div style={{ marginLeft: "7px" }} key={item.id}>
          //           <Tag>{getFirstNonNullValue(item.name)}</Tag>
          //         </div>
          //       );
          //     })}
          //   </div>
          // ) : (
          //   "-"
          // ),
          <Badge tone={item.is_active ? "success" : "info"}>
            {item.is_active ? "Active" : "Draft"}
          </Badge>,
          <RowActions
            actions={[
              { content: "Edit", onAction: () => handleEdit(item.id) },
              {
                content: "Lectures",
                onAction: () => handleEditLectures(item.id),
              },
              { content: "Notes", onAction: () => handleEditNotes(item.id) },
              {
                content: "Subscribers",
                onAction: () => handleSubscribers(item.id),
              },
              {
                content: "Q&A",
                onAction: () => handleEditQuestions(item.id),
              },
              {
                content: "Duplicate",
                onAction: () => handleDuplicate(item.id),
              },
            ]}
          />,
        ]),
        isLoading: false,
        isListEmpty: !applyVideoFilter(responseCourses.data.data.data).length,
        tableTotalPages: applyVideoFilter(responseCourses.data.data.data).length,
      });
    } catch (error) {
      console.log(error);
    }

    // if (!featuresList.length)
    //   try {
    //     responseFeatures = await axios.get(`features`);
    //     setFeaturesList(
    //       responseFeatures.data.data.data.map((item, index) => {
    //         return {
    //           label: item.name,
    //           value: item.id,
    //         };
    //       })
    //     );
    //   } catch (error) {
    //     console.log(error);
    //   }
  }

  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive((active) => !active), []);
  const [toastContent, setToastContent] = useState(
    "This course has been successfully duplicated"
  );

  const toastMarkup = active ? (
    <Toast content={toastContent} onDismiss={toggleActive} />
  ) : null;

  return (
    <Page
      title="Courses"
      fullWidth
      primaryAction={
        <Button variant="primary" onClick={handleAddCourse}>
          Add Course
        </Button>
      }
      // backAction={{
      //   onAction: () => navigate(`/admin/universities/${id}/subjects`),
      // }}
    >
      <Card padding="0">
        {loadingMarkup}
        <FormLayout>
          <div style={{ paddingRight: "1rem", paddingLeft: "1rem" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "0.75rem",
                paddingTop: "0.75rem",
              }}
            >
              <Select
                label="Type"
                options={[
                  { label: "All types", value: "" },
                  { label: "School", value: "1" },
                  { label: "University", value: "2" },
                ]}
                onChange={handleSelectChangeType}
                value={selectedType}
              />
              <Select
                label="University"
                options={[
                  { label: "All universities", value: "" },
                  ...optionsUniversities,
                ]}
                onChange={handleSelectChangeUniversity}
                value={selectedUniversity}
              />
              <Select
                label="Subject"
                options={[
                  { label: "All subjects", value: "" },
                  ...optionsSubjects,
                ]}
                onChange={handleSelectChangeSubject}
                value={selectedSubject}
              />
              <Select
                label="Status"
                options={[
                  { label: "All statuses", value: "" },
                  { label: "Active", value: "1" },
                  { label: "Draft", value: "0" },
                ]}
                onChange={handleSelectChangeStatus}
                value={selectedStatus}
              />
              <Select
                label="Videos"
                options={[
                  { label: "Any", value: "" },
                  { label: "Has videos", value: "with" },
                  { label: "No videos", value: "without" },
                ]}
                onChange={handleSelectChangeVideos}
                value={selectedVideos}
              />
            </div>
            <Filters
              queryValue={queryValue}
              queryPlaceholder="Search..."
              filters={filters}
              appliedFilters={appliedFilters}
              onQueryChange={handleFiltersQueryChange}
              onQueryClear={handleQueryValueRemove}
              onClearAll={handleFiltersClearAll}
            />
            {hasActiveFilters ? (
              <div style={{ paddingBottom: "0.75rem" }}>
                <Button onClick={handleClearAllFilters}>
                  Clear all filters
                </Button>
              </div>
            ) : null}
          </div>
          {/* <FormLayout.Group>
            <FormLayout>
              <Text>University</Text>
              <Select2
                options={optionsUniversities}
                onChange={handleSelectChangeUniversity}
                value={selectedUniversity}
                placeholder="Please select a university"
                styles={{
                  // Fixes the overlapping problem of the component
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
              />
            </FormLayout>
          </FormLayout.Group> */}
          {/* <Filters
            queryValue={queryValue}
            queryPlaceholder="Search..."
            filters={filters}
            appliedFilters={appliedFilters}
            onQueryChange={handleFiltersQueryChange}
            onQueryClear={handleQueryValueRemove}
            onClearAll={handleFiltersClearAll}
          /> */}

          <DataTable
            columnContentTypes={["text", "text", "text", "text"]}
            sortable={[
              true,
              false,
              true,
              false,
              true,
              false,
              false,
              false,
              false,
            ]}
            defaultSortDirection="descending"
            initialSortColumnIndex={sortColumnIndex}
            onSort={handleSort}
            headings={[
              <Text variant="bodyMd" as="p" fontWeight="medium">
                ID
              </Text>,
              <Text variant="bodyMd" as="p" fontWeight="medium">
                Image
              </Text>,
              <Text variant="bodyMd" as="p" fontWeight="medium">
                Title
              </Text>,

              <Text variant="bodyMd" as="p" fontWeight="medium">
                Videos
              </Text>,
              <Text variant="bodyMd" as="p" fontWeight="medium">
                Price (KWD)
              </Text>,
              <Text variant="bodyMd" as="p" fontWeight="medium">
                Institution
              </Text>,
              <Text variant="bodyMd" as="p" fontWeight="medium">
                Subject
              </Text>,
              <Text variant="bodyMd" as="p" fontWeight="medium">
                Status
              </Text>,
              <Text variant="bodyMd" as="p" fontWeight="medium"></Text>,
            ]}
            rows={pageObject.tableItems}
            verticalAlign="middle"
            increasedTableDensity
            footerContent={
              pageObject.tableItems?.length &&
              `Showing ${pageObject.tableItems.length} of ${pageObject.tableTotalPages} results`
            }
            hasZebraStripingOnData
            // verticalAlign="middle"
          />
        </FormLayout>
        {emtyState}
        {Math.ceil(pageObject.tableTotalPages / perPage) >= 2 && (
          <div
            style={{
              paddingTop: "10px",
              textAlign: "center",
              paddingBottom: "10px",
            }}
          >
            <Pagination
              variant="outlined"
              count={Math.ceil(pageObject.tableTotalPages / perPage)}
              page={page}
              onChange={handleChangePage}
            />
            {/* <Pagination
              count={Math.ceil(pageObject.tableTotalPages / perPage)}
              page={page}
              onChange={handleChangePage}
              size="large"
            /> */}
          </div>
        )}

        <Modal
          open={activeDuplicate}
          onClose={handleChangeModalDuplicate}
          title="Duplicate Course"
          primaryAction={{
            content: "Submit",
            onAction: handleYesDuplicate,
            loading: isSaving,
          }}
          secondaryActions={[
            {
              content: "Cancel",
              loading: isSaving,
              onAction: handleChangeModalDuplicate,
            },
          ]}
        >
          <Modal.Section>
            <TextField
              value={courseName}
              onChange={handleChangeCourseName}
              label="Title"
              error={courseNameError}
            />
          </Modal.Section>
        </Modal>
        {toastMarkup}
      </Card>
    </Page>
  );
  function disambiguateLabel(key, value) {
    switch (key) {
      case "taggedWith":
        return `Tagged with ${value}`;
      case "availability":
        return `Filter by ${value}`;
      default:
        return value;
    }
  }
  function isEmpty(value) {
    if (Array.isArray(value)) {
      return value.length === 0;
    } else {
      return value === "" || value == null;
    }
  }
  function getFirstNonNullValue(obj) {
    for (const value of Object.values(obj)) {
      if (value !== null) {
        return value;
      }
    }
    return null;
  }
  // `time` is the summed duration of every playlist in the course, maintained
  // by the backend as videos are added and removed. No lectures at all is a
  // second, independent signal that there is nothing to watch.
  function hasVideos(item) {
    const hasLectures = Array.isArray(item?.lectures) && item.lectures.length > 0;
    return hasLectures && Number(item?.time) > 0;
  }
  function applyVideoFilter(items) {
    if (selectedVideos === "with") return items.filter(hasVideos);
    if (selectedVideos === "without") return items.filter((i) => !hasVideos(i));
    return items;
  }
  // Course durations are stored in milliseconds.
  function formatDuration(ms) {
    const total = Math.round(Number(ms || 0) / 1000);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    return hours ? `${hours}h ${minutes}m` : `${minutes}m`;
  }
  // Universities, schools and classes were three mostly-empty columns; the
  // relevant one is shown inline with the type badge instead.
  function institutionLabel(item) {
    if (item.type?.description === "University") {
      return item.university ? getFirstNonNullValue(item.university.name) : "-";
    }
    if (item.type?.description === "School") {
      const school = item.school ? getFirstNonNullValue(item.school.name) : null;
      const klass = item.school_class
        ? getFirstNonNullValue(item.school_class.name)
        : null;
      return [school, klass].filter(Boolean).join(" · ") || "-";
    }
    return "-";
  }
};

export default Courses;
